'use client';

import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { useBuilderStore } from '@/store/builderStore';
import Input from '@/app/components/ui/Input';
import Select from '@/app/components/ui/Select';
import Button from '@/app/components/ui/Button';
import { MATERIALS, FINISHES } from '@/types/product';
import { Layers, Palette, ArrowRight, ArrowLeft, Ruler, Weight } from 'lucide-react';

interface FormData {
  material: string;
  finish: string;
  color: string;
  height: string;
  width: string;
  depth: string;
  weight: string;
}

const materialOptions = MATERIALS.map((m) => ({ value: m, label: m }));
const finishOptions = FINISHES.map((f) => ({ value: f, label: f }));

const materialIcons: Record<string, string> = {
  Brass: '🟡',
  Copper: '🟠',
  Wood: '🪵',
  Iron: '⚙️',
  Steel: '🔩',
  Marble: '🪨',
  Ceramic: '🏺',
  Glass: '🔮',
};

export default function MaterialSelector() {
  const { material, dimensions, setMaterial, setDimensions, setCurrentStep } = useBuilderStore();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      material: material.material,
      finish: material.finish,
      color: material.color,
      height: dimensions.height,
      width: dimensions.width,
      depth: dimensions.depth,
      weight: dimensions.weight,
    },
  });

  const selectedMaterial = watch('material');
  const selectedFinish = watch('finish');

  const onSubmit = (data: FormData) => {
    setMaterial({
      material: data.material,
      finish: data.finish,
      color: data.color,
    });
    setDimensions({
      height: data.height,
      width: data.width,
      depth: data.depth,
      weight: data.weight,
    });
    setCurrentStep(3);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
            <Layers size={18} className="text-white" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-800 tracking-tight">Material & Finish</h2>
            <p className="text-sm text-slate-400">Choose the perfect material and finish</p>
          </div>
        </div>

        {/* Material & Finish Grid */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-4 rounded-full bg-amber-500" />
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest">
              Material Selection
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Material"
              required
              placeholder="Select material"
              options={materialOptions}
              error={errors.material?.message}
              {...register('material', { required: 'Material is required' })}
            />
            <Select
              label="Finish"
              required
              placeholder="Select finish"
              options={finishOptions}
              error={errors.finish?.message}
              {...register('finish', { required: 'Finish is required' })}
            />
          </div>

          <Input
            label="Color"
            placeholder="e.g., Deep Gold, Midnight Black, Pearl White"
            leftIcon={<Palette size={16} />}
            error={errors.color?.message}
            {...register('color')}
          />
        </div>

        {/* Material Preview Cards */}
        {selectedMaterial && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl">{materialIcons[selectedMaterial] || '🔷'}</span>
              <div>
                <p className="font-bold text-slate-800">
                  {selectedMaterial} {selectedFinish && `• ${selectedFinish}`}
                </p>
                <p className="text-xs text-slate-500">
                  Premium quality {selectedMaterial.toLowerCase()} with{' '}
                  {selectedFinish ? selectedFinish.toLowerCase() : 'selected'} finish
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Dimensions */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-1 h-4 rounded-full bg-amber-500" />
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest">
              Dimensions
            </h3>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Input
              label="Height"
              type="number"
              placeholder="cm"
              min="0"
              step="0.1"
              leftIcon={<Ruler size={14} />}
              hint="cm"
              error={errors.height?.message}
              {...register('height', {
                min: { value: 0, message: 'Must be positive' },
              })}
            />
            <Input
              label="Width"
              type="number"
              placeholder="cm"
              min="0"
              step="0.1"
              leftIcon={<Ruler size={14} />}
              hint="cm"
              error={errors.width?.message}
              {...register('width', {
                min: { value: 0, message: 'Must be positive' },
              })}
            />
            <Input
              label="Depth"
              type="number"
              placeholder="cm"
              min="0"
              step="0.1"
              leftIcon={<Ruler size={14} />}
              hint="cm"
              error={errors.depth?.message}
              {...register('depth', {
                min: { value: 0, message: 'Must be positive' },
              })}
            />
            <Input
              label="Weight"
              type="number"
              placeholder="kg"
              min="0"
              step="0.01"
              leftIcon={<Weight size={14} />}
              hint="kg"
              error={errors.weight?.message}
              {...register('weight', {
                min: { value: 0, message: 'Must be positive' },
              })}
            />
          </div>

          <p className="text-xs text-slate-400 flex items-center gap-1">
            <span>ℹ️</span> All dimensions in centimeters (cm), weight in kilograms (kg)
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-between pt-2">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={() => setCurrentStep(1)}
            className="gap-2"
          >
            <ArrowLeft size={18} />
            Back
          </Button>
          <Button type="submit" size="lg" className="gap-2 min-w-40">
            Next Step
            <ArrowRight size={18} />
          </Button>
        </div>
      </form>
    </motion.div>
  );
}
